<template>
  <div id="invite-accept">
    <div class="invite-shell">

      <div v-if="loading" class="invite-card text-center">
        <i class="fas fa-spinner fa-spin fa-2x text-muted"></i>
        <p class="mt-3 text-muted">Loading invitation…</p>
      </div>

      <div v-else-if="error" class="invite-card">
        <div class="invite-icon-bad">
          <i class="fas fa-exclamation-circle"></i>
        </div>
        <h3 class="text-center">We can't open this invitation</h3>
        <p class="text-center text-muted">{{ error }}</p>
        <div class="text-center mt-3">
          <router-link to="/" class="btn btn-secondary">
            Return to Every.Farm
          </router-link>
        </div>
      </div>

      <div v-else-if="resolved" class="invite-card">
        <div class="invite-icon-good">
          <i :class="resolved === 'accept' ? 'fas fa-check-circle' : 'fas fa-info-circle'"></i>
        </div>
        <h3 class="text-center" v-if="resolved === 'accept'">
          You're in!
        </h3>
        <h3 class="text-center" v-else>
          Invitation declined
        </h3>
        <p class="text-center text-muted" v-if="resolved === 'accept'">
          You now have <strong>{{ invite.role }}</strong> access to
          <strong>{{ invite.farmName || 'this farm' }}</strong>.
        </p>
        <p class="text-center text-muted" v-else>
          We've let the sender know you'd rather not join.
        </p>
        <div class="text-center mt-3">
          <router-link
            v-if="resolved === 'accept' && invite.farmId"
            :to="`/f/${invite.farmId}`"
            class="btn btn-success"
          >
            Open {{ invite.farmName || 'farm' }}
          </router-link>
          <router-link
            v-else
            to="/"
            class="btn btn-secondary"
          >
            Return to Every.Farm
          </router-link>
        </div>
      </div>

      <div v-else class="invite-card">
        <div class="invite-icon-info">
          <i class="fas fa-envelope-open-text"></i>
        </div>
        <p class="text-center text-muted mb-1" v-if="invite.invitedByEmail">
          {{ invite.invitedByEmail }} invited you
        </p>
        <h3 class="text-center mb-2">
          Join <span class="text-success">{{ invite.farmName || 'this farm' }}</span>
        </h3>
        <p class="text-center text-muted">
          You'll join as <strong>{{ invite.role }}</strong>.
        </p>
        <p class="text-center text-muted small mt-2" v-if="expiresHuman">
          This invitation expires {{ expiresHuman }}.
        </p>
        <div v-if="emailMismatch" class="alert alert-warning mt-3">
          <strong>Heads up:</strong> this invitation is addressed to
          <code>{{ invite.email }}</code> but you're signed in as
          <code>{{ currentEmail }}</code>. Sign in with the invited
          email to accept.
        </div>
        <div class="invite-actions">
          <b-btn
            variant="success"
            :disabled="acting || emailMismatch"
            @click="respond('accept')"
          >
            <i class="fas fa-check" v-if="!acting"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            Accept
          </b-btn>
          <b-btn
            variant="outline-secondary"
            :disabled="acting"
            @click="respond('decline')"
          >
            Decline
          </b-btn>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
const fb = require('../../firebaseConfig.js')
// See lib/callable.js — bypasses the v7 SDK's broken httpsCallable
// auth flow that fires `code: 'internal'` before reaching the network.
const { callCallable } = require('../../lib/callable.js')

export default {
  name: 'InviteAccept',
  data () {
    return {
      loading: true,
      error: null,
      invite: null,
      acting: false,
      resolved: null // null | 'accept' | 'decline'
    }
  },
  computed: {
    inviteId () {
      return this.$route.params.id
    },
    currentEmail () {
      const u = fb.auth.currentUser
      return (u && u.email && u.email.toLowerCase()) || ''
    },
    emailMismatch () {
      if (!this.invite || !this.invite.email) return false
      return this.currentEmail && this.currentEmail !== this.invite.email
    },
    expiresHuman () {
      if (!this.invite || !this.invite.expiresAt) return ''
      const date = this.toDate(this.invite.expiresAt)
      if (!date) return ''
      const diffMs = date.getTime() - Date.now()
      if (diffMs < 0) return 'this invitation has expired'
      const days = Math.round(diffMs / (24 * 3600 * 1000))
      if (days <= 0) return 'today'
      if (days === 1) return 'tomorrow'
      return `in ${days} days`
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const snap = await fb.db.collection('invitations').doc(this.inviteId).get()
        if (!snap.exists) {
          this.error = "We couldn't find this invitation — it may have been cancelled or already used."
          this.loading = false
          return
        }
        const invite = { id: snap.id, ...snap.data() }
        const uid = fb.auth.currentUser && fb.auth.currentUser.uid
        if (invite.status === 'accepted' && uid && invite.claimedByUid === uid) {
          // Accepted by THIS user — most commonly autoClaimInvitesOnSignup
          // applied it during signup and the post-login redirect landed them
          // back here. Show the success card, not an error.
          this.invite = invite
          this.resolved = 'accept'
          this.loading = false
          return
        }
        if (invite.status === 'accepted') {
          this.error = 'This invitation has already been accepted.'
        } else if (invite.status === 'declined') {
          this.error = 'This invitation was already declined.'
        } else if (invite.status === 'cancelled') {
          this.error = 'This invitation was cancelled by the sender.'
        } else if (invite.status === 'expired') {
          this.error = 'This invitation has expired.'
        } else {
          const expiresAt = this.toDate(invite.expiresAt)
          if (expiresAt && expiresAt < new Date()) {
            this.error = 'This invitation has expired.'
          }
        }
        this.invite = invite
      } catch (err) {
        console.log('invite load error:', err)
        this.error = "We couldn't load this invitation. You may need to sign in with the email it was sent to."
      }
      this.loading = false
    },

    async respond (action) {
      if (this.acting) return
      this.acting = true
      try {
        await callCallable('respondToInvitation', { inviteId: this.inviteId, action })
        this.resolved = action
        if (action === 'accept' && this.invite && this.invite.farmId) {
          // Best-effort: refresh local user/farm state so the
          // Farms list reflects the new membership on next render.
          try {
            await this.$store.dispatch('fetchFarms')
          } catch (e) { /* ignore */ }
        }
      } catch (err) {
        console.log('respond error:', err)
        this.error = (err && err.message) || 'Something went wrong responding to this invitation.'
      }
      this.acting = false
    },

    toDate (timestamp) {
      if (!timestamp) return null
      if (timestamp instanceof Date) return timestamp
      if (typeof timestamp === 'number') return new Date(timestamp)
      if (typeof timestamp === 'string') {
        const d = new Date(timestamp)
        return isNaN(d.getTime()) ? null : d
      }
      if (typeof timestamp === 'object') {
        if (typeof timestamp.toDate === 'function') {
          try { return timestamp.toDate() } catch (e) { /* fall through */ }
        }
        if (typeof timestamp._seconds === 'number') {
          return new Date(timestamp._seconds * 1000)
        }
        if (typeof timestamp.seconds === 'number') {
          return new Date(timestamp.seconds * 1000)
        }
      }
      return null
    }
  }
}
</script>

<style scoped>
#invite-accept {
  position: fixed;
  inset: 0;
  background: #f4f7fb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.invite-shell {
  width: 100%;
  max-width: 480px;
}
.invite-card {
  background: #fff;
  border: 1px solid #d6dbe0;
  border-radius: 10px;
  padding: 32px 28px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}
.invite-icon-info,
.invite-icon-good,
.invite-icon-bad {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin: 0 auto 16px;
}
.invite-icon-info { background: rgba(6, 103, 57, 0.10); color: #066739; }
.invite-icon-good { background: #d4edda; color: #155724; }
.invite-icon-bad  { background: #f8d7da; color: #721c24; }
.invite-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 24px;
}
.invite-actions .btn { min-width: 110px; }
</style>
